import 'react-app-polyfill/ie11'
import 'react-app-polyfill/stable'
import React from 'react'
import ReactDOM from 'react-dom'
import { Route, Router, Switch } from 'react-router-dom'
import { MuiPickersUtilsProvider } from '@material-ui/pickers'
import { CssBaseline, MuiThemeProvider } from '@material-ui/core'
import 'intersection-observer'
import { AuthContextProvider, authRoutes } from 'packages/core'
import { Loader, NotificationsProvider } from 'packages/eid-ui'
import * as serviceWorker from './serviceWorker'
import { AppContextProvider } from 'appContext'
import AppRoutes from 'pages/AppRoutes'
import './i18n'
import 'packages/eid-ui/globalStyles.css'
import config from 'config'
import { ReactQueryConfigProvider } from 'react-query'
import { ReactQueryDevtools } from 'react-query-devtools'
import MomentUtils from '@date-io/moment'
import theme from 'theme'
import 'moment/locale/de'
import 'moment/locale/fr'
import 'moment/locale/es'
import './index.css'
import { ApplicationInsights } from '@microsoft/applicationinsights-web'
import {
    ReactPlugin,
    withAITracking,
} from '@microsoft/applicationinsights-react-js'
import { createBrowserHistory } from 'history'

const reactQueryConfig = {
    queries: {
        refetchOnMount: false,
        refetchOnWindowFocus: false,
    },
}

const history = createBrowserHistory()

let reactPlugin

if (
    config.APP_INSIGHTS_KEY &&
    config.APP_INSIGHTS_KEY !== '#{APP_INSIGHTS_KEY}#'
) {
    reactPlugin = new ReactPlugin()
    const appInsights = new ApplicationInsights({
        config: {
            instrumentationKey: config.APP_INSIGHTS_KEY,
            extensions: [reactPlugin],
            extensionConfig: {
                [reactPlugin.identifier]: { history },
            },
        },
    })
    appInsights.loadAppInsights()
}

const AppWithAI = reactPlugin
    ? withAITracking(reactPlugin, AppRoutes)
    : AppRoutes

const MOUNT_NODE = document.getElementById('root')

const authConfig = {
    settings: {
        authority: config.AUTHORITY_URL,
        client_id: config.CLIENT_ID,
        scope: config.SCOPE,
        response_type: config.RESPONSE_TYPE ?? 'code',
    },
    initialValue: {
        displayName: '',
        currentUserId: '',
    },
    extractAuthData: async (token) => {
        const rawResponse = await fetch(`${config.API_URL}/api/user`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        })
        const response = await rawResponse.json()
        return {
            friendlyName: response.displayName,
            currentUserId: response.personId,
        }
    },
}

const MainApp = (
    <React.StrictMode>
        <Router history={history}>
            <MuiThemeProvider theme={theme}>
                <CssBaseline />
                <MuiPickersUtilsProvider utils={MomentUtils}>
                    <ReactQueryConfigProvider config={reactQueryConfig}>
                        <AuthContextProvider config={authConfig}>
                            <AppContextProvider>
                                <NotificationsProvider>
                                    <Switch>
                                        {authRoutes.map((ar) => (
                                            <Route
                                                key={ar.path}
                                                exact
                                                path={ar.path}
                                                render={(props) => (
                                                    <ar.component
                                                        {...props}
                                                        render={() => (
                                                            <Loader />
                                                        )}
                                                    />
                                                )}
                                            />
                                        ))}
                                        <AppWithAI />
                                    </Switch>
                                </NotificationsProvider>
                            </AppContextProvider>
                        </AuthContextProvider>
                    </ReactQueryConfigProvider>
                </MuiPickersUtilsProvider>
            </MuiThemeProvider>
        </Router>
        {process.env.NODE_ENV === 'development' && <ReactQueryDevtools />}
    </React.StrictMode>
)

ReactDOM.render(MainApp, MOUNT_NODE)

serviceWorker.unregister()
